import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import ArrowIcon from "./ArrowIcon";
import CardContent from "./CardContent";

export default function EventTile({
  titleLabel,
  events,
  columnIndex,
  columnMeta,
  onTitleClick,
  sharedEventIndex,
  isFullscreen,
  exitFullscreen,
  onAddToSlot,
  planDayDates = null,
  planSlots = null,
  getSlotKey = null,
}) {
  const [, setSearchParams] = useSearchParams();

  const [index, setIndex] = useState(sharedEventIndex ?? 0);
  const [nextIndex, setNextIndex] = useState(null);
  const [direction, setDirection] = useState("");
  const [animating, setAnimating] = useState(false);

  const columnIdForUrls =
    columnMeta?.id != null && columnMeta.id !== "" ? String(columnMeta.id) : String(columnIndex);
  const isStoryColumn = columnMeta?.type === "story";

  // sync with shared links
  useEffect(() => {
    if (sharedEventIndex !== null) {
      setIndex(sharedEventIndex);
    }
  }, [sharedEventIndex]);

  const startSwipe = (dir) => {
    if (animating || events.length === 0) return;
    const newIndex =
      dir === "left"
        ? (index + 1) % events.length
        : (index - 1 + events.length) % events.length;
    setDirection(dir);
    setNextIndex(newIndex);
    setAnimating(true);

    const eventId = events[newIndex]?.id ?? newIndex;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("column", columnIdForUrls);
        next.set("event", String(eventId));
        return next;
      },
      { replace: true },
    );
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => startSwipe("left"),
    onSwipedRight: () => startSwipe("right"),
    trackMouse: true,
  });

  const handleAnimationEnd = () => {
    if (nextIndex !== null) {
      setIndex(nextIndex);
      setNextIndex(null);
      setDirection("");
      setAnimating(false);
    }
  };

  const currentEvent = events[index];
  const nextEventData = nextIndex !== null ? events[nextIndex] : null;

  return (
    <div {...handlers} className="tile">
      <h2 className="tile-header" onClick={onTitleClick}>
        {isFullscreen && (
          <span
            className="back-arrow"
            onClick={(e) => {
             e.stopPropagation();
              exitFullscreen();
            }}
          >
            ←
          </span>
        )}
        {titleLabel}
      </h2>

      <div className="event-container">
        <button
          className="nav-arrow left-arrow"
          aria-label="Previous"
          onClick={(e) => {
            e.stopPropagation();
            startSwipe("right");
          }}
        >
          <ArrowIcon direction="left" />
        </button>

        {currentEvent && (
          <div
            className={`event-card current ${animating ? `slide-out-${direction}` : ""}`}
            onAnimationEnd={handleAnimationEnd}
          >
            <CardContent
              data={currentEvent}
              isStory={isStoryColumn}
              columnIndex={columnIndex}
              columnId={columnIdForUrls}
              index={index}
              onAddToSlot={onAddToSlot}
              listLabel={titleLabel}
              planDayDates={planDayDates}
              planSlots={planSlots}
              getSlotKey={getSlotKey}
            />
          </div>
        )}

        {nextEventData && (
          <div className={`event-card next slide-in-${direction}`}>
            <CardContent
              data={nextEventData}
              isStory={isStoryColumn}
              columnIndex={columnIndex}
              columnId={columnIdForUrls}
              index={nextIndex}
              onAddToSlot={onAddToSlot}
              listLabel={titleLabel}
              planDayDates={planDayDates}
              planSlots={planSlots}
              getSlotKey={getSlotKey}
            />
          </div>
        )}

        <button
          className="nav-arrow right-arrow"
          aria-label="Next"
          onClick={(e) => {
            e.stopPropagation();
            startSwipe("left");
          }}
        >
          <ArrowIcon direction="right" />
        </button>
      </div>
    </div>
  );
}